import {
  createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode
} from 'react'
import { IconX, IconAlertTriangle, IconCircleCheck, IconInfoCircle, IconExclamationCircle } from './icons'

// Lightweight toast system. Command results (errors + selected confirmations) surface here
// instead of being silently dropped. Provider sits above TelephonyProvider so useCommand
// (which needs both) can reach it.

export type ToastKind = 'info' | 'success' | 'warn' | 'error'
export interface ToastItem { id: number; kind: ToastKind; message: string }

interface ToastApi {
  push: (message: string, kind?: ToastKind, ttlMs?: number) => void
  dismiss: (id: number) => void
}

const Ctx = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const seq = useRef(0)

  const dismiss = useCallback((id: number) => {
    setItems((xs) => xs.filter((t) => t.id !== id))
  }, [])

  const push = useCallback((message: string, kind: ToastKind = 'info', ttlMs = 5000) => {
    const id = ++seq.current
    setItems((xs) => [...xs, { id, kind, message }])
    if (ttlMs > 0) window.setTimeout(() => dismiss(id), ttlMs)
  }, [dismiss])

  const api = useMemo<ToastApi>(() => ({ push, dismiss }), [push, dismiss])

  return (
    <Ctx.Provider value={api}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} />
    </Ctx.Provider>
  )
}

const ICON: Record<ToastKind, typeof IconInfoCircle> = {
  info: IconInfoCircle, success: IconCircleCheck, warn: IconAlertTriangle, error: IconExclamationCircle
}

function ToastViewport({ items, onDismiss }: { items: ToastItem[]; onDismiss: (id: number) => void }) {
  if (items.length === 0) return null
  return (
    <div className="sb-toasts" role="region" aria-label="Notifications">
      {items.map((t) => {
        const Icon = ICON[t.kind]
        return (
          <div key={t.id} className={`sb-toast sb-toast-${t.kind}`} role="status" aria-live="polite">
            <span className="sb-toast-ico"><Icon size={16} stroke={1.9} /></span>
            <span className="sb-toast-msg">{t.message}</span>
            <button className="sb-toast-x" aria-label="Dismiss" onClick={() => onDismiss(t.id)}>
              <IconX size={13} stroke={2} />
            </button>
          </div>
        )
      })}
    </div>
  )
}

export function useToast(): ToastApi {
  const v = useContext(Ctx)
  if (!v) throw new Error('useToast must be used within <ToastProvider>')
  return v
}
